/**
 * Here are global state for ContractValidationEdit.tsx
 */
import type { ReactNode } from "react";
import { createContext, useContext, useRef } from "react";
import type { Agreement } from "store/agreementsStore";
import type { IUser } from "types";

interface IAgreementPageState {
    /**
     * Agreement data
     */
    agreement: Agreement | null;
    /**
     * Agreement publisher signer
     */
    agreementPublisherSigner: IUser | undefined;
    /**
     * If the current user is the agreement creator
     */
    isOwner: boolean;
    /**
     * If the agreement is draft
     */
    isDraft: boolean;
    /**
     * If the agreement is signed
     */
    isSigned: boolean;
    /**
     * If the current user can edit the publisher signer (owner or admin, and draft/negotiating)
     */
    hasAbilityToEditPublisherSigner: boolean;
}

interface IAgreementPageContext extends IAgreementPageState {
    rightPanelRef: React.RefObject<HTMLDivElement>;
}

interface IAgreementPageStateProviderProps extends IAgreementPageState {
    children: ReactNode;
}

const AgreementPageContext = createContext<IAgreementPageContext | undefined>(undefined);

export const AgreementPageStateProvider: React.FC<IAgreementPageStateProviderProps> = ({
    children,
    agreement,
    agreementPublisherSigner,
    isOwner,
    isDraft,
    isSigned,
    hasAbilityToEditPublisherSigner,
}: IAgreementPageStateProviderProps) => {
    const rightPanelRef = useRef<HTMLDivElement>(null);

    const value: IAgreementPageContext = {
        rightPanelRef,
        agreement,
        agreementPublisherSigner,
        isOwner,
        isDraft,
        isSigned,
        hasAbilityToEditPublisherSigner,
    };

    return <AgreementPageContext.Provider value={value}>{children}</AgreementPageContext.Provider>;
};

// Hook to access context
export function useAgreementPageContext() {
    const context = useContext(AgreementPageContext);
    if (!context) {
        throw new Error("useAgreementPageContext must be used within an AgreementPageStateProvider");
    }
    return context;
}
